import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Newspaper, ExternalLink, Award } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';

const MediaSection = () => {
  const { media } = useSiteData();
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });

  if (!media || media.length === 0) return null;

  return (
    <section id="media" ref={ref} className="py-24 bg-gradient-to-br from-orange-50 via-white to-slate-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>मीडिया कवरेज</h2>
          <p className="text-xl text-orange-600 font-semibold" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            मीडिया कवरेज
          </p>
          <p className="text-gray-600 mt-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>समाचार पत्रों, टीवी साक्षात्कारों और ऑनलाइन समाचारों में</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {media.map((item, index) => (
            <motion.a
              key={item.id}
              href={item.link || '#'}
              target={item.link ? '_blank' : undefined}
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group block"
              data-testid={`media-card-${index}`}
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 h-full flex flex-col">
                {item.image && (
                  <div className="relative aspect-video overflow-hidden">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-sm text-orange-600 font-semibold mb-2">
                    <Newspaper className="h-4 w-4" />
                    <span>{item.outlet}</span>
                    {item.date && <span className="text-gray-400">• {item.date}</span>}
                  </div>
                  <h3 className="text-lg font-bold text-blue-900 mb-2 group-hover:text-orange-600 transition-colors">
                    {item.title}
                  </h3>
                  {item.excerpt && <p className="text-gray-600 text-sm flex-1">{item.excerpt}</p>}
                  {item.link && (
                    <div className="flex items-center gap-1 text-orange-600 text-sm mt-4 font-medium" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
                      और पढ़ें <ExternalLink className="h-3 w-3" />
                    </div>
                  )}
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MediaSection;
